import { catalogNameFromAccountKey, deriveAccountKeyFromDims } from '@server/warehouse-keys';

/**
 * Join requirement clauses into a natural-language list ("a", "a and b",
 * "a, b, and c"). Used by the forms to phrase the "what's still missing" hint
 * shown in the Setup SQL section.
 */
export function joinClauses(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** Normalize an arbitrary label into a safe uppercase identifier segment (alphanumerics + `_`). */
function normalizeIdent(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * What a Snowflake service user's and role's generated names are derived from (ACT2-1116). The key
 * fingerprint completes it, and only the form has that, so the prefill carries the seed rather than
 * finished names.
 */
export interface SnowflakeNameSeed {
  /** The workspace slug (`workspaces.id`), which never changes after creation. */
  workspaceSlug: string;
  /** The server-normalized account key, so two spellings of one account give one name. */
  accountKey: string;
}

/** The seed for a bucket, or null until the bucket names an account the server would key. */
export function snowflakeNameSeed(workspaceSlug: string, hostDim: string, dbDim: string): SnowflakeNameSeed | null {
  const accountKey = deriveAccountKeyFromDims('SNOWFLAKE', hostDim, dbDim);
  if (!workspaceSlug || !accountKey) return null;
  return { workspaceSlug, accountKey };
}

/** RFC 4648 base32: unambiguous upper case, valid in an unquoted Snowflake identifier. */
const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** The display part of a generated name: the account key's database, at most 24 characters. */
const DB_LABEL_MAX = 24;

/**
 * `SHA-256(slug ␀ accountKey ␀ fingerprint)`, top 40 bits, as 8 base32 characters.
 *
 * Any input change gives a new suffix: a regenerated key pair, another account, another workspace.
 * The user id is not an input, so two admins of one workspace get the same names, and a re-run is
 * safe. The destination name is not an input either: it is a label, and it can change.
 */
export async function snowflakeNameSuffix(seed: SnowflakeNameSeed, fingerprint: string): Promise<string> {
  const input = new TextEncoder().encode(`${seed.workspaceSlug}\u0000${seed.accountKey}\u0000${fingerprint}`);
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', input));
  // 40 bits = 5 bytes = 8 groups of 5 bits. 40 bits fit a Number exactly, so no BigInt is needed.
  let bits = 0;
  for (const byte of bytes.subarray(0, 5)) bits = bits * 256 + byte;
  let suffix = '';
  for (let group = 7; group >= 0; group--) suffix += BASE32[Math.floor(bits / 2 ** (group * 5)) % 32];
  return suffix;
}

export interface SnowflakeServiceNames {
  username: string;
  role: string;
}

/** `LOOKOUT_<DB>_<SFX>_SVC` and `LOOKOUT_<DB>_<SFX>_ROLE`; `<DB>` is only there to be readable. */
export async function deriveSnowflakeServiceNames(
  seed: SnowflakeNameSeed,
  fingerprint: string,
): Promise<SnowflakeServiceNames> {
  const suffix = await snowflakeNameSuffix(seed, fingerprint);
  const db = normalizeIdent(catalogNameFromAccountKey(seed.accountKey) ?? '')
    .slice(0, DB_LABEL_MAX)
    .replace(/_+$/, '');
  const stem = db ? `LOOKOUT_${db}_${suffix}` : `LOOKOUT_${suffix}`;
  return { username: `${stem}_SVC`, role: `${stem}_ROLE` };
}

/**
 * Whether a name is one of the defaults the form used to prefill before ACT2-1116
 * (`LOOKOUT_<DESTINATION>_SERVICE_USER`, `LOOKOUT_<WORKSPACE>_ROLE`). The form treats such a value as
 * untouched and replaces it with the derived name.
 */
export function isLegacySnowflakeDefault(field: 'username' | 'role', value: string, workspaceSlug: string): boolean {
  const name = value.trim().toUpperCase();
  if (field === 'username') return /^LOOKOUT_(?:[A-Z0-9_]+_)?SERVICE_USER$/.test(name);
  const ident = normalizeIdent(workspaceSlug);
  return name === (ident ? `LOOKOUT_${ident}_ROLE` : 'LOOKOUT_ROLE');
}

/** Redshift managed-password service username, derived from the destination name (lowercased). */
export function defaultRedshiftServiceUsername(destinationName: string): string {
  const ident = normalizeIdent(destinationName).toLowerCase();
  return ident ? `lookout_${ident}_service_user` : 'lookout_service_user';
}
